import * as React from 'react';
import styled, {css} from 'styled-components';
import {darken, transparentize} from 'polished';
import outline from 'helpers/outline';
import {transition} from 'helpers/css';
import {Theme} from '../stores/AppStore';

export enum ButtonVariant {
  Default = 'default',
  Primary = 'primary',
  Outline = 'outline',
  Secondary = 'secondary',
  Danger = 'Danger',
}

export enum ButtonScale {
  Small = 'small',
  Medium = 'medium',
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  scale?: ButtonScale;
  stretched?: boolean;
}

export const Container = styled.div<Pick<ButtonProps,
  | 'stretched'
  | 'disabled'
  | 'variant'
  | 'scale'>>`
  gap: 8px;
  align-items: center;
  grid-auto-flow: column;
  justify-content: center;
  display: ${({stretched}) => stretched ? 'grid' : 'inline-grid'};
  width: ${({stretched}) => stretched ? '100%' : 'auto'};

  background: ${({disabled, theme}) => disabled && `
    ${theme.variables.accentBackgroundColor};
  `};

  color: ${({disabled, theme}) => disabled && `
    ${transparentize(.9, theme.colors.white)};
  `};

  ${({scale}) => {
    switch (scale) {
      case ButtonScale.Small: return css`
        font-weight: 500;
        font-size: 12px;
        border-radius: 5px;
        padding: 7px 12px;
      `;
      case ButtonScale.Medium: return css`
        line-height: 18px;
        font-weight: 500;
        font-size: 14px;
        
        border-radius: 5px;
        padding: 13px 24px;
      `;
    }
  }};
  
  ${({variant, theme}) => {
    switch (variant) {
      case ButtonVariant.Default: return css`
        background: ${({theme}) => transparentize(.95, theme.colors.white)};
        
        &:hover {
          background: ${({theme}) => transparentize(.9, theme.colors.white)};
        }
        
        &:active {
          background: ${({theme}) => transparentize(.85, theme.colors.white)};
        }
      `;
      case ButtonVariant.Primary: return css`
        color: ${({theme}) => theme.colors.white};
        background: ${({theme}) => theme.colors.primary};
      `;
      case ButtonVariant.Outline: return css`
        background: transparent;
        border: ${({theme}) => theme.name === Theme.Dark ? `
            1px solid ${transparentize(.85, theme.colors.white)};
        ` : `
            1px solid ${transparentize(.6, theme.variables.color)};
        `};
        
        &:hover {
          background: ${({theme}) => transparentize(.98, theme.colors.white)};
        }
        
        &:active {
          background: ${({theme}) => transparentize(.95, theme.colors.white)};
        }
      `;
      case ButtonVariant.Secondary: return css`
        background: transparent;
        
        &:hover {
          background: ${({theme}) => transparentize(.98, theme.colors.white)};
        }
        
        &:active {
          background: ${({theme}) => transparentize(.95, theme.colors.white)};
        }
      `;
      case ButtonVariant.Danger: return css`
        background: ${({theme}) => darken(.03, theme.colors.red)};
      `;
    }
  }};
  
  cursor: ${({disabled}) => disabled && 'not-allowed'};
  
  ${transition()};
  
  &:focus-visible {
    ${outline(6, 2)};
  }
`;

/**
 * The simple button component
 * @param {ButtonProps} props
 * @return {React.ReactElement<ButtonProps>}
 * @constructor
 */
function Button(
    props: ButtonProps,
): React.ReactElement<ButtonProps> {
  // @ts-ignore
  return <Container as="button" {...props} />;
}

Button.defaultProps = {
  variant: ButtonVariant.Default,
  scale: ButtonScale.Medium,
};

export default Button;
